/**
 * Is it this prompt's turn? The React binding for lib/firstRunPrompts.
 *
 * One hook for every optional first-run ask. It gathers the facts the schedule
 * cannot know on its own (which prompts this build and account can show at
 * all) from the providers every prompt already sits under, and follows the
 * schedule's store so a claim or an answer re-decides at once.
 *
 * The prompt itself keeps its delay, its yields to open modals, and its copy.
 * When its timer fires it calls claimFirstRunPrompt; when it is answered or
 * dismissed, markFirstRunPromptDone.
 *
 * The store rating is the odd one out (2026-10-26): it is never shown on a
 * timer, so it has no card and no "due" hook. useRequestRating hands a
 * surface a callback to fire at its moment of success; the schedule decides
 * inside the callback, with the same availability facts as the cards.
 */
import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import { useAuth, useAuthOptional } from "@/components/AuthProvider";
import { useSession, useSessionOptional } from "@/components/SessionProvider";
import { isNative } from "./platform";
import { getLiveEventPrefs, useLiveEventPrefs } from "./liveEventPrefs";
import { canLeaveStoreReview, requestRatingAtMoment, type RatingAskOutcome, type RatingMoment } from "./storeReview";
import { ratingLog } from "./ratingLog";
import {
  accountCreatedMs,
  getFirstRunVersion,
  pickDuePrompt,
  subscribeFirstRunPrompts,
  type FirstRunPromptId,
} from "./firstRunPrompts";

/**
 * Which prompts this build and this account can show at all: the review only
 * where a store review can be left, the OS sheet only on a phone, the Pro
 * sheet only to a settled Basic trainer, attribution only while the account
 * has no answer. One function, so the cards and the rating sheet agree.
 */
export function firstRunPromptAvailability(args: {
  heardAboutAnswered: boolean;
  tier: string | null | undefined;
  tierSettled: boolean | null | undefined;
  /** The account already holds a live-events choice (set here or on another phone). */
  liveEventsConfigured?: boolean;
}): Partial<Record<FirstRunPromptId, boolean>> {
  return {
    "live-events": args.liveEventsConfigured !== true,
    "heard-about": !args.heardAboutAnswered,
    notifications: isNative(),
    // "free" is the one tier without Pro access (hasProAccess's complement);
    // the boot placeholder is "free" too, hence the settled check.
    "why-pro": args.tierSettled === true && args.tier === "free",
    review: canLeaveStoreReview(),
  };
}

export function useFirstRunPromptDue(id: FirstRunPromptId): boolean {
  const { account } = useAuth();
  const { tier, tierSettled } = useSession();
  const version = useSyncExternalStore(subscribeFirstRunPrompts, getFirstRunVersion, () => 0);
  const createdAt = account?.createdAt;
  const heardAboutAnswered = account?.heardAboutAnswered === true;
  const signedIn = account != null;
  // A live-events choice on this device (hydrated from the account on
  // sign-in, lib/liveEventPrefs) means the set-up sheet has had its answer.
  const liveEventsConfigured = useLiveEventPrefs() != null;
  return useMemo(() => {
    if (!signedIn) return false;
    const due = pickDuePrompt({
      accountCreatedAt: accountCreatedMs(createdAt),
      available: firstRunPromptAvailability({ heardAboutAnswered, tier, tierSettled, liveEventsConfigured }),
    });
    return due === id;
    // `version` is the store's write counter: a claim or an answer re-decides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, signedIn, createdAt, heardAboutAnswered, tier, tierSettled, liveEventsConfigured, version]);
}

/**
 * Something is in front of the app that an OS sheet must not land on: the
 * paywall, the legal splash, the onboarding, the tour, the Nex questionnaire,
 * the trainer step. Null (no provider) counts as nothing open.
 */
type SessionLike = NonNullable<ReturnType<typeof useSessionOptional>>;

export function sessionModalOpen(
  s: Pick<
    SessionLike,
    "paywallOpen" | "legalOpen" | "onboardingOpen" | "tourOpen" | "nexQuestionnaireOpen" | "trainerStepOpen"
  > | null,
): boolean {
  if (!s) return false;
  return s.paywallOpen || s.legalOpen || s.onboardingOpen || s.tourOpen || s.nexQuestionnaireOpen || s.trainerStepOpen;
}

/**
 * A callback for a moment of success: ask for the store rating sheet if the
 * schedule allows it (lib/storeReview.requestRatingAtMoment). Reads the
 * providers through refs, so it may be called from a timer or after an await
 * without a stale account or tier. Resolves "skipped" when signed out, when a
 * modal is in front (sessionModalOpen), or when the schedule says no.
 *
 * Both providers are optional, since the scan menu can mount without them.
 */
export function useRequestRating(): (moment: RatingMoment) => Promise<RatingAskOutcome> {
  const auth = useAuthOptional();
  const session = useSessionOptional();
  const authRef = useRef(auth);
  const sessionRef = useRef(session);
  authRef.current = auth;
  sessionRef.current = session;
  return useCallback(async (moment) => {
    const account = authRef.current?.account;
    const s = sessionRef.current;
    if (!account) {
      ratingLog(`skipped at ${moment}: signed out`);
      return "skipped";
    }
    if (sessionModalOpen(s)) {
      ratingLog(`skipped at ${moment}: a modal is in front`);
      return "skipped";
    }
    return requestRatingAtMoment(moment, {
      accountCreatedAt: accountCreatedMs(account.createdAt),
      available: firstRunPromptAvailability({
        heardAboutAnswered: account.heardAboutAnswered === true,
        tier: s?.tier,
        tierSettled: s?.tierSettled,
        liveEventsConfigured: getLiveEventPrefs() != null,
      }),
    });
  }, []);
}
